import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Route guard: only renders children if the current user's role is in
 * `allow`. When `superAdminOnly` is set, requires the user to be the
 * application-level super-admin (founder). While auth is loading, renders
 * nothing. If the user is not authenticated at all, redirects to /login
 * (matches DashboardLayout). Otherwise redirects to /dashboard with a query
 * flag so the target page can toast "Insufficient permissions".
 */
export const RequireRole: React.FC<{
  allow?: Array<"admin" | "staff" | "user">;
  superAdminOnly?: boolean;
  platformStaffOk?: boolean;
  children: React.ReactNode;
}> = ({ allow, superAdminOnly, platformStaffOk, children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  // Platform-admin surfaces: App Super Admin OR a Super Staff / Super User.
  if (platformStaffOk) {
    const ok = user.is_super_admin || user.platform_role === "super_staff" || user.platform_role === "super_user";
    if (!ok) return <Navigate to="/dashboard?forbidden=1" replace />;
    return <>{children}</>;
  }
  if (superAdminOnly && !user.is_super_admin) {
    return <Navigate to="/dashboard?forbidden=1" replace />;
  }
  if (allow) {
    const role = (user.role || "user") as "admin" | "staff" | "user";
    if (!allow.includes(role)) {
      return <Navigate to="/dashboard?forbidden=1" replace />;
    }
  }
  return <>{children}</>;
};
